import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";
import { trackCtaClick, getEligibilityUrl } from "@/lib/analytics";

/**
 * Sticky nav appears on scroll — glass morphism bar, ultra-thin.
 * Hidden at top, fades in once scrolled past hero threshold.
 */
const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 80, 200], [0, 0.85, 0.95]);
  const visible = useTransform(scrollY, [0, 200], [0, 1]);
  const translateY = useTransform(scrollY, [0, 200], [-16, 0]);

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#how", label: t.nav.processus, key: "processus" },
    { href: "#services", label: t.nav.services, key: "services" },
    { href: "#testimonials", label: t.nav.testimonials, key: "testimonials" },
    { href: "/about", label: t.footer.about, key: "about" },
    { href: "/faq", label: t.footer.faq, key: "faq" },
    { href: "#contact", label: t.nav.contact, key: "contact" },
  ];

  const langs = ["fr", "en"];

  return (
    <motion.header
      data-testid="navbar"
      className="fixed top-0 inset-x-0 z-50"
      style={{
        opacity: visible,
        y: translateY,
        backdropFilter: scrolled ? "blur(22px)" : "none",
      }}
    >
      <motion.div
        className="absolute inset-0 bg-[#0D1117]"
        style={{ opacity }}
      />
      <div className="absolute inset-x-0 bottom-0 h-px bg-[#E8A045]/15" />

      <nav className="relative max-w-[1320px] mx-auto px-6 lg:px-10 h-[64px] flex items-center justify-between">
        {/* Compact wordmark */}
        <a
          href="#top"
          data-testid="navbar-logo"
          dir="ltr"
          className="flex items-baseline gap-0 select-none font-display"
        >
          <span className="text-2xl text-[#9B7548] font-normal tracking-tight">
            Maro
          </span>
          <span className="text-2xl italic text-[#F5F2EE] font-normal tracking-tight">
            canada
          </span>
        </a>

        {/* Nav links — DM Mono accent */}
        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              className="font-mono-accent text-[10px] text-[#F5F2EE]/70 hover:text-[#E8A045] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-5">
          {/* hairline language toggle */}
          <div
            className="hidden sm:flex items-center gap-3 text-xs"
            data-testid="language-toggle"
          >
            {langs.map((l, i) => (
              <div key={l} className="flex items-center gap-3">
                <button
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`font-mono-accent text-[10px] transition-colors ${
                    lang === l
                      ? "text-[#E8A045]"
                      : "text-[#F5F2EE]/40 hover:text-[#F5F2EE]/70"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
                {i < langs.length - 1 && (
                  <span className="block w-px h-3 bg-[#F5F2EE]/15" />
                )}
              </div>
            ))}
          </div>

          <a
            href={getEligibilityUrl(lang)}
            data-testid="navbar-get-started"
            onClick={() => trackCtaClick("navbar")}
            className="inner-border-btn inline-flex items-center px-5 py-2.5 rounded-full bg-[#2FBFAD] hover:bg-[#239B8C] text-[#0D1117] text-xs font-medium tracking-wider transition-colors font-mono-accent"
          >
            {t.nav.getStarted}
          </a>
        </div>
      </nav>
    </motion.header>
  );
};

export default Navbar;
